import {
  BodyText,
  Card,
  Flex,
  Heading,
  Link,
  Strong,
} from '@utilitywarehouse/hearth-react';

import {
  EnergyFuelType,
  EnergyMeterType,
  EnergyPaymentMethod,
  EnergyPaymentOption,
  EnergyQuoteDetails,
} from '@generated/gql/graphql';

import { hasPrepaymentMeter, hasServices } from '../utils';
import { DetailsSkeleton } from './DetailsSkeleton';
import { UsageRow } from './UsageRow';
import './styles.scss';

interface RowProps {
  details: EnergyQuoteDetails;
}

interface PaymentMethodRowProps {
  details: EnergyQuoteDetails;
  fuelType: EnergyFuelType;
}

interface EnergyDetailsProps {
  details: EnergyQuoteDetails;
  fuelType: EnergyFuelType;
  isLoading: boolean;
  onEdit: () => void;
}

const getEnergyLabel = ({
  hasElectricity,
  hasGas,
}: {
  hasElectricity: boolean;
  hasGas: boolean;
}) => {
  if (hasElectricity && hasGas) {
    return 'electricity and gas';
  }

  if (hasGas) {
    return 'gas';
  }

  if (hasElectricity) {
    return 'electricity';
  }

  return 'N/A';
};

const getDenyText = (hasValue: boolean) => (hasValue ? '' : 'not');

const PaymentMethodRow = ({ details, fuelType }: PaymentMethodRowProps) => {
  const { electricityPaymentMethod, gasPaymentMethod } = details;
  const isDual = fuelType === EnergyFuelType.DualFuel;

  const renderContent = () => {
    if (!hasPrepaymentMeter(details)) {
      return (
        <BodyText>
          I <Strong>do not</Strong> have a prepayment meter
        </BodyText>
      );
    }

    if (isDual) {
      if (gasPaymentMethod !== EnergyPaymentMethod.Prepayment) {
        return (
          <BodyText>
            I <Strong>do</Strong> have a prepayment meter for electricity
          </BodyText>
        );
      }

      if (electricityPaymentMethod !== EnergyPaymentMethod.Prepayment) {
        return (
          <BodyText>
            I <Strong>do</Strong> have a prepayment meter for gas
          </BodyText>
        );
      }
    }

    return (
      <BodyText>
        I <Strong>do</Strong> have a prepayment meter
      </BodyText>
    );
  };

  return <li>{renderContent()}</li>;
};

const PaymentOptionRow = ({ details }: RowProps) => {
  const { paymentOption } = details;

  const renderPaymentOption = () => {
    if (paymentOption === EnergyPaymentOption.BudgetPlan) {
      return (
        <span>
          with a <Strong>budget plan</Strong>
        </span>
      );
    }

    return <Strong>monthly in arrears</Strong>;
  };

  return (
    <li>
      <BodyText>I’ll pay for my energy {renderPaymentOption()}</BodyText>
    </li>
  );
};

const EnergyDetails = ({
  details,
  fuelType,
  isLoading,
  onEdit,
}: EnergyDetailsProps) => {
  const { hasElectricity, hasGas } = hasServices(fuelType);

  const renderDetails = () => {
    if (isLoading) {
      return <DetailsSkeleton />;
    }

    const hasDetails = hasElectricity || hasGas;
    const isBothFuelsPrepayment =
      details.electricityPaymentMethod === EnergyPaymentMethod.Prepayment &&
      details.gasPaymentMethod === EnergyPaymentMethod.Prepayment;

    return (
      <Card
        className="energy__Details-paper"
        variant="subtle"
        colorScheme="neutralStrong"
        direction="column"
      >
        <div className="energy__Details-header">
          <Heading as="h3" size="md">
            My energy
          </Heading>
          <Flex onClick={onEdit}>
            <Link
              data-testid="energy__Details-edit"
              className="energy__Details-edit"
            >
              Edit
            </Link>
          </Flex>
        </div>
        {hasDetails && (
          <ul className="energy__Details-list">
            <li data-testid="energy-fuel-list">
              <BodyText>
                I need{' '}
                <Strong>
                  {getEnergyLabel({
                    hasElectricity,
                    hasGas,
                  })}
                </Strong>
              </BodyText>
            </li>
            <UsageRow fuelType={fuelType} details={details} />
            <PaymentMethodRow details={details} fuelType={fuelType} />
            {hasElectricity && (
              <li>
                <BodyText>
                  I{' '}
                  <Strong>
                    do{' '}
                    {getDenyText(
                      details.meterType === EnergyMeterType.Economy_7
                    )}
                  </Strong>{' '}
                  have an economy 7 meter
                </BodyText>
              </li>
            )}
            {!isBothFuelsPrepayment && <PaymentOptionRow details={details} />}
          </ul>
        )}
        {!hasDetails && <Strong>N/A</Strong>}
      </Card>
    );
  };

  return (
    <section>
      <Heading as="h2" size="lg">
        Check your details
      </Heading>
      {renderDetails()}
    </section>
  );
};

export { EnergyDetails };
